/**
 * Row Descriptor — the internal representation of a row's kind + config.
 *
 * Role is the governing axis: {source, layout, renderer, alias}.
 * Source rows own data (incl. derived / formula); layouts provide
 * rendering slots; renderers fill slots; aliases hydrate templates.
 *
 * Detail: `sheets/.docs/content/rows-variants.md`.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject } from "../core/json.ts";
import { Model } from "@tento-core/model";
import { WireDecodeError } from "../wire/wire-decode.ts";
import type {
  CellCardinality,
  SourceShape,
  Dtype,
} from "../core/dtype.ts";
import type { LayoutKind, RendererId } from "./slot.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

/**
 * The role a row plays in a sheet: it holds data (`source`), lays out,
 * renders, aliases a template, or is inert.
 */
export type Role = "source" | "layout" | "renderer" | "alias" | "inert";

/** Values that go with `Role`: `ALL`, `is`, `of`. */
export namespace Role {
  /** Every role, in declaration order. */
  export const ALL: readonly Role[] = deepFrozen(["source", "layout", "renderer", "alias", "inert"]);

  /** Whether `raw` spells a role. */
  export function is(raw: string): raw is Role {
    return ALL.some((role) => role === raw);
  }

  /**
   * The role `raw` spells; throws `WireDecodeError` (type `"Role"`) for any
   * other string — the decoder a serialized role is read back through.
   */
  export function of(raw: string): Role {
    if (is(raw)) return raw;
    throw new WireDecodeError("Role", [
      Model.issue([], "enum", `expected one of ${ALL.join(" | ")}, got ${JSON.stringify(raw)}`),
    ]);
  }
}

/**
 * Source row — `dataSource`. Owns or computes data. Transparent in
 * rendering; the hydrated renderer child paints.
 *
 * Descriptors are **immutable** — they define the row's coarse
 * type axes (role, dtype, sourceShape, cellCardinality). Mutable
 * per-row state (`label`, `formula`, `lookupKey`, `encoding`,
 * renderer attrs, …) lives on `RowMetadataStore`; user edits go
 * there, not here.
 *
 * Cell **shape** is NOT declared on the descriptor. Provider rows
 * carry server-owned shape metadata; formula and user rows may still
 * be recognized by probing cells through the `ShapeRegistry`.
 *
 * Cell **backing** (who fills the cells — user / provider /
 * formula) is likewise NOT a descriptor concern. It's a runtime
 * property: canonical provider rows are identified by a `:~`-
 * prefixed lookupKey (see `lona-rows/src/routes.rs`); formula-
 * derived rows are `FormulaDataSource` instances with an expression.
 * The same `dtype="obj"` descriptor can host user writes, a
 * provider push, or a formula emitting an Event / Weather shape.
 */
/**
 * A retired row type kept recognizable as a tombstone: `"calendar"`, the
 * pre-alias calendar row. Such a row keeps its type as its renderer
 * override, and draws as an explicit Unknown Plugin under that name instead
 * of silently becoming text, data or a partially working calendar.
 */
export type RetiredRendererType = "calendar";

export interface SourceDescriptor {
  readonly role: "source";
  readonly dtype: Dtype;
  readonly sourceShape: SourceShape;
  readonly cellCardinality: CellCardinality;
  /**
   * The renderer this source is drawn with instead of its default one: a
   * slot-qualified `RendererId`, or a retired row type's name
   * (`RetiredRendererType`), which draws as an explicit Unknown Plugin.
   */
  readonly rendererOverride?: RendererId | RetiredRendererType;
  /**
   * Precise wire subtype for `dtype: "obj"` rows (`"weather"`, `"tasks"`,
   * `"list"`, …). role+dtype+sourceShape alone collapse every obj source to
   * the generic `"data-source"`; this preserves the exact wire type so
   * `wireTypeOf` stays precise without a meta.compat round-trip. Undefined on
   * non-obj source rows (their type is derivable from the axes).
   */
  readonly objSubtype?: string;
  /**
   * Case 4 iteration driver. When a multi-card formula row declares
   * `formulaSource`, the engine interprets the formula body as a
   * lambda applied once per cell of the named source row at each
   * evaluation date (multi → multi is a per-cell map; single → multi
   * fan-out is rejected). Undefined for single-card formulas and non-formula rows.
   */
  readonly formulaSource?: string;
}

/**
 * Layout row — rendering container. `timeline[row]` and
 * `timeline[column]` are distinct layout types and are NOT
 * interchangeable.
 *
 * Mutable config (`filter`, canvas axes) lives on
 * `RowMetadataStore`, not here.
 */
export interface LayoutDescriptor {
  readonly role: "layout";
  readonly layout: LayoutKind;
}

/**
 * Inert row — display-only block that owns no cells and does not
 * subsume children. Used for structural chrome like labels, group
 * pass-throughs, sheet footers, and timeline legends. Rendered
 * directly by a layout-style plugin.
 *
 * The `kind` field carries the plugin identity (e.g. "block",
 * "label", "sheet-footer") while the descriptor role distinguishes
 * inert rows from slot-bearing layout rows.
 */
export interface InertDescriptor {
  readonly role: "inert";
  readonly kind: string;
}

/**
 * Renderer row — fills a single slot. Named `<layout>.<renderer>`
 * (e.g. `column.text`, `canvas.hrule`, `timeline[row].event`).
 *
 * Mutable renderer attributes (color, offset, radius, …) live in
 * the row's `attributes` bag on `RowMetadataStore`.
 */
export interface RendererDescriptor {
  readonly role: "renderer";
  readonly rendererId: RendererId;
}

/**
 * Alias row — Figma-style template instance. Transparent in
 * rendering; hydrates children from the named template.
 *
 * `template` is immutable (it defines *which* template this alias
 * instantiates). Mutable instance state — props, overrides, the
 * detached `hydrated` flag — lives on `AliasInstanceStore`.
 *
 * Detail: `sheets/.docs/content/rows-aliases.md`.
 */
export interface AliasDescriptor {
  readonly role: "alias";
  readonly template: string;
}

/**
 * What a row is, by role: a source, layout, renderer, alias or inert row's
 * descriptor.
 */
export type Descriptor =
  | SourceDescriptor
  | LayoutDescriptor
  | RendererDescriptor
  | AliasDescriptor
  | InertDescriptor;

/**
 * Derived-source transform step. Serializable by construction — no
 * closures. Pipeline is applied in order.
 */
export type Transform =
  | { readonly kind: "scale"; readonly factor: number }
  | {
      readonly kind: "clamp";
      readonly min?: number;
      readonly max?: number;
    }
  | {
      readonly kind: "convertUnits";
      readonly from: string;
      readonly to: string;
    }
  | { readonly kind: "filter"; readonly where: string }
  | {
      readonly kind: "aggregate";
      readonly window: string;
      readonly fn: "sum" | "mean" | "min" | "max" | "count";
    };

/** Canvas axis config, held on a `canvas` layout row. */
export interface CanvasConfig {
  readonly xAxis: "mse" | "dse" | "hours";
  readonly xPadding?: { readonly days?: number };
  readonly yAxis: {
    readonly id: string;
    readonly min?: number;
    readonly max?: number;
    readonly label?: string;
  };
}

/** Values that go with `CanvasConfig`: `decode`, `toJson`. */
export namespace CanvasConfig {
  const X_AXES: readonly CanvasConfig["xAxis"][] = ["mse", "dse", "hours"];

  /** Decode a `canvas` attribute value; `null` unless it has `xAxis` and a `yAxis.id`. */
  export function decode(value: Option<JsonObject>): Option<CanvasConfig> {
    const xAxis = Json.oneOf(value, "xAxis", X_AXES);
    const yAxis = Json.child(value, "yAxis");
    const id = Json.string(yAxis, "id");
    if (xAxis == null || id == null) return null;
    const min = Json.number(yAxis, "min");
    const max = Json.number(yAxis, "max");
    const label = Json.string(yAxis, "label");
    const padding = Json.child(value, "xPadding");
    const days = Json.number(padding, "days");
    return {
      xAxis,
      ...(padding != null ? { xPadding: days != null ? { days } : {} } : {}),
      yAxis: {
        id,
        ...(min != null ? { min } : {}),
        ...(max != null ? { max } : {}),
        ...(label != null ? { label } : {}),
      },
    };
  }

  /** The JSON form `decode` reads. */
  export function toJson(config: CanvasConfig): JsonObject {
    const days = config.xPadding?.days;
    return {
      xAxis: config.xAxis,
      ...(config.xPadding !== undefined
        ? { xPadding: days !== undefined ? { days } : {} }
        : {}),
      yAxis: {
        id: config.yAxis.id,
        ...(config.yAxis.min !== undefined ? { min: config.yAxis.min } : {}),
        ...(config.yAxis.max !== undefined ? { max: config.yAxis.max } : {}),
        ...(config.yAxis.label !== undefined ? { label: config.yAxis.label } : {}),
      },
    };
  }
}
